import type { FormInstance } from '@arco-design/web-react/es/Form';
import type { RulesProps } from '@arco-design/web-react/es/Form/interface';
import { get } from 'lodash';

import { MaasModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  getModelSizeStorageMismatchErrorKey,
  getModelSourceType,
  readModelInstanceFormValues,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import { hasModelSourceCore } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelSource';
import { readModelSourcesFromForm } from '@/delivery-type-plugin/capabilities/maas-model-storage/useModelSourcesList';
import { getDataDiskMode } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { getPrimaryDataVolume } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import i18n from '@/i18n';

import { areModelSourcesSameType, getModelSourceTypeFromEntry } from './modelSources';

const t = i18n.t.bind(i18n);

function requiredFieldMessage(fieldKey: string): string {
  return t('common:validate.required', { name: t(fieldKey) });
}

function validateModelSizeAgainstStorage(form: FormInstance, callback: (error?: string) => void): boolean {
  const errorKey = getModelSizeStorageMismatchErrorKey(readModelInstanceFormValues(form));
  if (errorKey) {
    callback(i18n.t(errorKey));
    return true;
  }
  return false;
}

/** 是否已有来源核心信息（仓库跳转展示卡片，大小可后续补全） */
export function isModelSourcePrefilled(source: Record<string, unknown> | undefined, sourceType?: MaasModelSourceType): boolean {
  if (!source || !sourceType) return false;
  return hasModelSourceCore(source, sourceType);
}

/** 校验当前模型来源是否已完整填写 */
export function isModelSourceComplete(
  source: Record<string, unknown> | undefined,
  sourceType?: MaasModelSourceType,
  modelSize?: unknown,
): boolean {
  if (!source || !sourceType || !String(modelSize ?? '').trim()) return false;
  if (!hasModelSourceCore(source, sourceType)) return false;
  if (sourceType === MaasModelSourceType.S3) {
    return !!String(get(source, 's3.secretRef.accessKeyID') || '').trim() && !!String(get(source, 's3.secretRef.secretKey') || '').trim();
  }
  return true;
}

/** 弹窗确认前校验模型字段（创建/已有数据盘共用） */
export async function validateStorageModalModelFields(form: FormInstance, isCreateStorage: boolean): Promise<void> {
  const fields = isCreateStorage ? ['model.source', 'model.name', 'model.size'] : ['model.name', 'model.size'];
  await form.validate(fields);
}

/** 添加模型弹窗确认前校验（含服务名、大小） */
export async function validateAddModelModalFields(form: FormInstance): Promise<void> {
  await form.validate(['model.source', 'model.name', 'model.size']);
  const existing = readModelSourcesFromForm(form);
  if (!existing.length) return;
  const lockedType = getModelSourceTypeFromEntry(existing[0]);
  const draftType = getModelSourceType(form.getFieldValue('model.source') as Record<string, unknown> | undefined);
  if (lockedType && draftType && lockedType !== draftType) {
    throw new Error(i18n.t('data-center:maas.model.sources.same.type.required'));
  }
}

/** 模型实例多源：步骤级 model + storage 校验 */
export function validateMultiSourceModelStorage(form: FormInstance, callback: (error?: string) => void): void {
  const volume = getPrimaryDataVolume(form);
  if (!volume || !getDataDiskMode(volume)) {
    callback(i18n.t('data-center:maas.storage.configure.first'));
    return;
  }
  const sources = readModelSourcesFromForm(form);
  if (!sources.length) {
    callback(requiredFieldMessage('data-center:maas.model.select'));
    return;
  }
  const allNamed = sources.every((entry) => String(entry.name ?? '').trim());
  if (!allNamed) {
    callback(requiredFieldMessage('data-center:maas.service.name'));
    return;
  }
  const allSized = sources.every((entry) => String(entry.size ?? '').trim());
  if (!allSized) {
    callback(i18n.t('data-center:maas.model.size.required'));
    return;
  }
  if (!areModelSourcesSameType(sources)) {
    callback(i18n.t('data-center:maas.model.sources.same.type.required'));
    return;
  }
  if (validateModelSizeAgainstStorage(form, callback)) return;
  callback();
}

/** 已有 PVC：弹窗内填写 model.name / model.size，加载路径在推理与服务模块 */
function getExistingPvcModelFormRules(form: FormInstance): RulesProps[] {
  return [
    {
      validator(_, callback) {
        const name = String(form.getFieldValue('model.name') || '').trim();
        const modelSize = form.getFieldValue('model.size');
        if (!name) {
          callback(requiredFieldMessage('data-center:maas.service.name'));
          return;
        }
        if (!String(modelSize ?? '').trim()) {
          callback(i18n.t('data-center:maas.model.size.required'));
          return;
        }
        if (validateModelSizeAgainstStorage(form, callback)) return;
        callback();
      },
    },
  ];
}

/** 创建数据盘弹窗内 model.source 校验：允许预填来源（size 可后续补全），与 ModelSourceSetting 展示逻辑一致 */
export function getCreateStorageModalModelRules(form: FormInstance): RulesProps[] {
  return [
    {
      validator(_, callback) {
        const source = form.getFieldValue('model.source') as Record<string, unknown> | undefined;
        const modelSize = form.getFieldValue('model.size');
        const type = getModelSourceType(source);
        if (!isModelSourceComplete(source, type, modelSize) && !isModelSourcePrefilled(source, type)) {
          callback(requiredFieldMessage('data-center:maas.model.select'));
          return;
        }
        callback();
      },
    },
  ];
}

/** 模型选择区块表单校验（步骤提交时展示错误提示） */
function getModelSourceFormRules(form: FormInstance): RulesProps[] {
  return [
    {
      validator(_, callback) {
        const source = form.getFieldValue('model.source') as Record<string, unknown> | undefined;
        const modelSize = form.getFieldValue('model.size');
        const type = getModelSourceType(source);
        if (!String(modelSize ?? '').trim()) {
          callback(i18n.t('data-center:maas.model.size.required'));
          return;
        }
        if (!isModelSourceComplete(source, type, modelSize)) {
          callback(requiredFieldMessage('data-center:maas.model.select'));
          return;
        }

        const name = String(form.getFieldValue('model.name') || '').trim();
        if (!name) {
          callback(requiredFieldMessage('data-center:maas.service.name'));
          return;
        }
        if (validateModelSizeAgainstStorage(form, callback)) return;
        callback();
      },
    },
  ];
}

/** 按存储模式校验模型字段（步骤提交用） */
export function validateModelFieldsByStorageMode(
  form: FormInstance,
  isCreateStorage: boolean,
  callback: (error?: string) => void,
  options?: { multiSource?: boolean },
): void {
  if (options?.multiSource) {
    validateMultiSourceModelStorage(form, callback);
    return;
  }
  const rules = isCreateStorage ? getModelSourceFormRules(form) : getExistingPvcModelFormRules(form);
  rules[0].validator?.(null, (error) => {
    if (error) {
      callback(typeof error === 'string' ? error : String(error));
      return;
    }
    callback();
  });
}
